import { useState, useMemo } from "react";
import {
  Warehouse, Loader2, AlertTriangle, Send, Pencil, MoreVertical,
  Package, Calendar, Settings2, ChevronDown, Box,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { formatCameraAssegnata } from "../../utils/formatCamera";
import { ASL_SCADENZA_GIORNI } from "../../utils/primaNotaHelpers";

const TIPI_AUSILIO = [
  { key: "pannoloni", label: "Pannoloni", iconBg: "bg-violet-100", iconColor: "text-violet-600" },
  { key: "traverse", label: "Traverse", iconBg: "bg-blue-100", iconColor: "text-blue-600" },
];

const AVATAR_PALETTE = [
  { bg: "bg-violet-100", text: "text-violet-700" },
  { bg: "bg-blue-100", text: "text-blue-700" },
  { bg: "bg-teal-100", text: "text-teal-700" },
  { bg: "bg-rose-100", text: "text-rose-700" },
];

function guestInitials(nome) {
  return (nome || "?")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function avatarStyle(ospiteId) {
  const n = [...String(ospiteId)].reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_PALETTE[n % AVATAR_PALETTE.length];
}

function formatData(data) {
  if (!data) return "—";
  try {
    const [y, m, d] = data.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return data;
  }
}

function ScadenzaBadge({ giorni }) {
  if (giorni == null) return null;
  if (giorni < 0) {
    return (
      <Badge className="bg-red-100 text-red-700 border-0 text-[10px] font-semibold shrink-0">
        SCADUTA
      </Badge>
    );
  }
  if (giorni <= ASL_SCADENZA_GIORNI) {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-0 text-[10px] font-semibold shrink-0">
        {giorni} gg
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px] font-semibold shrink-0">
      {giorni} gg
    </Badge>
  );
}

function AusilioRow({ tipoConfig, data }) {
  const c = data;
  if (!c || (c.consumoGiornaliero || 0) === 0) return null;
  const gg = c.giorniAllaScadenza;
  const pacchi = c.pacchiDaOrdinare || 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(200px,240px)_minmax(200px,260px)] gap-4 py-4 border-t border-slate-100 first:border-t-0 first:pt-0">
      {/* Colonna 1 — Dettaglio ausilio */}
      <div className="flex gap-3 min-w-0">
        <div
          className={`w-12 h-12 rounded-xl ${tipoConfig.iconBg} flex items-center justify-center shrink-0`}
        >
          <Package className={`w-6 h-6 ${tipoConfig.iconColor}`} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <h4 className="font-bold text-slate-900 text-base mb-2">{tipoConfig.label}</h4>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Consumo giornaliero</dt>
              <dd className="font-medium text-slate-800">{c.consumoGiornaliero} pz/giorno</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Fabbisogno mensile</dt>
              <dd className="font-medium text-slate-800">{c.fabbisognoMensile} pz</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Fornitura ASL/trimestre</dt>
              <dd className="font-medium text-slate-800">{c.fornituraAslTrimestrale || 0} pz</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-slate-500">Copertura ASL/mese</dt>
              <dd className="font-medium text-slate-800">{c.coperturaAslMensile} pz</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-red-600 font-medium">Deficit mensile</dt>
              <dd className="font-bold text-red-600">{c.deficitMensile} pz</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Colonna 2 — Pacchi da ordinare */}
      <div className="rounded-xl bg-orange-50 border border-orange-100 p-4 flex flex-col justify-center relative min-h-[140px]">
        <Box className="w-5 h-5 text-orange-400 absolute top-3 right-3" />
        <p className="text-sm font-bold text-orange-700">Pacchi da ordinare</p>
        <p className="text-2xl font-bold text-orange-600 mt-1 leading-tight">
          {pacchi} {pacchi === 1 ? "pacco" : "pacchi"}
        </p>
        <p className="text-xs text-orange-600/90 mt-2 leading-snug">
          Consigliato per coprire il deficit del mese.
        </p>
      </div>

      {/* Colonna 3 — Scadenza ASL */}
      <div className="rounded-xl bg-cyan-50 border border-cyan-100 p-4 flex flex-col justify-center min-h-[140px]">
        <p className="text-sm font-bold text-slate-800 mb-3">Scadenza fornitura ASL</p>
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
          <span className="font-semibold text-slate-800">
            {c.scadenzaAsl ? formatData(c.scadenzaAsl) : "—"}
          </span>
          <ScadenzaBadge giorni={gg} />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-slate-500">Fornitore</span>
          <Badge
            variant="outline"
            className={
              c.fornitore === "struttura"
                ? "border-violet-200 text-violet-700 bg-violet-50 text-xs font-medium"
                : "border-emerald-200 text-emerald-700 bg-emerald-50 text-xs font-medium"
            }
          >
            {c.fornitore === "struttura" ? "Struttura" : "Parente"}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function GuestMagazzinoCard({
  ospite,
  entry,
  sendingReminder,
  onReminder,
  onEdit,
  onRegisterSpesa,
  onOpenReport,
}) {
  const avatar = avatarStyle(ospite.id);
  const ultimoRem = entry?.ultimoReminderInviatoAt;
  const hasParente =
    (entry?.pannoloni?.fornitore === "parente" && (entry?.pannoloni?.deficitMensile || 0) > 0) ||
    (entry?.traverse?.fornitore === "parente" && (entry?.traverse?.deficitMensile || 0) > 0);
  const hasStruttura =
    (entry?.pannoloni?.fornitore === "struttura" && (entry?.pannoloni?.deficitMensile || 0) > 0) ||
    (entry?.traverse?.fornitore === "struttura" && (entry?.traverse?.deficitMensile || 0) > 0);

  const ausiliRows = TIPI_AUSILIO.map((t) => (
    <AusilioRow key={t.key} tipoConfig={t} data={entry?.[t.key]} />
  )).filter(Boolean);

  if (ausiliRows.length === 0) return null;

  return (
    <article className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 bg-white">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-11 h-11 rounded-full ${avatar.bg} ${avatar.text} flex items-center justify-center font-bold text-sm shrink-0`}
          >
            {guestInitials(ospite.nome_completo)}
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-slate-900 leading-tight">{ospite.nome_completo}</h3>
            {ospite.camera && (
              <p className="text-sm text-slate-500">
                ({formatCameraAssegnata(ospite.camera) || ospite.camera})
              </p>
            )}
            {ultimoRem && (
              <p className="text-[11px] text-slate-400 mt-0.5">
                Ultimo reminder: {formatData(String(ultimoRem).split("T")[0])}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {hasParente && (
            <Button
              size="sm"
              className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 text-xs h-9"
              disabled={sendingReminder === ospite.id}
              onClick={() => onReminder(ospite.id)}
            >
              {sendingReminder === ospite.id ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5 mr-1.5" />
              )}
              Reminder a parenti
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-9 w-9 text-slate-500"
            onClick={() => onEdit(ospite.id)}
            title="Modifica configurazione"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-9 w-9 text-slate-500">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {hasStruttura && (
                <DropdownMenuItem onClick={() => onRegisterSpesa(ospite.id)}>
                  Registra spesa struttura
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onOpenReport(ospite.id)}>
                Apri report mensile
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
      <div className="px-4 pb-4">{ausiliRows}</div>
    </article>
  );
}

const INITIAL_VISIBLE = 4;

export default function PrimaNotaMagazzinoTab({
  ospitiAttivi,
  magMap,
  loadingMag,
  aslAlerts,
  sendingReminder,
  onSendReminder,
  onOpenMagConfig,
  onRegisterSpesaFromMag,
  onOpenReport,
}) {
  const [showAll, setShowAll] = useState(false);

  const configuredGuests = useMemo(() => {
    return ospitiAttivi.filter((o) => {
      const entry = magMap[o.id];
      return (
        entry &&
        ((entry.pannoloni?.consumoGiornaliero || 0) > 0 ||
          (entry.traverse?.consumoGiornaliero || 0) > 0)
      );
    });
  }, [ospitiAttivi, magMap]);

  const unconfiguredGuests = useMemo(() => {
    return ospitiAttivi.filter((o) => !configuredGuests.some((c) => c.id === o.id));
  }, [ospitiAttivi, configuredGuests]);

  const visibleGuests = showAll
    ? configuredGuests
    : configuredGuests.slice(0, INITIAL_VISIBLE);
  const hasMore = configuredGuests.length > INITIAL_VISIBLE && !showAll;

  return (
    <>
      {aslAlerts.length > 0 && (
        <div className="mb-4 space-y-2">
          {aslAlerts.map((a, i) => (
            <div
              key={i}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium shadow-sm ${
                a.level === "expired"
                  ? "bg-red-50 border border-red-200 text-red-800"
                  : "bg-amber-50 border border-amber-200 text-amber-800"
              }`}
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                <strong>{a.nome}</strong> — Fornitura ASL {a.tipo}{" "}
                {a.level === "expired"
                  ? `scaduta da ${Math.abs(a.gg)} giorni`
                  : `in scadenza tra ${a.gg} giorni`}
              </span>
            </div>
          ))}
        </div>
      )}

      <Card className="border border-slate-200/90 shadow-sm rounded-2xl bg-white">
        <CardHeader className="border-b border-slate-100 rounded-t-2xl pb-4">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div>
              <CardTitle className="text-lg flex items-center gap-2 text-slate-900">
                <Warehouse className="w-5 h-5 text-rose-500" />
                Magazzino Ausili
              </CardTitle>
              <p className="text-sm text-slate-600 mt-1 max-w-xl">
                Visualizza i consumi, la copertura ASL e gestisci il fabbisogno per ogni ospite.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="border-rose-200 text-rose-600 hover:bg-rose-50"
              >
                <Send className="w-4 h-4 mr-1.5" />
                Reminder a parenti
              </Button>
              <Button size="icon" variant="outline" className="border-slate-200 text-slate-600">
                <Settings2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-5">
          {loadingMag ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-rose-500" />
            </div>
          ) : (
            <div className="space-y-4">
              {visibleGuests.map((ospite) => (
                <GuestMagazzinoCard
                  key={ospite.id}
                  ospite={ospite}
                  entry={magMap[ospite.id]}
                  sendingReminder={sendingReminder}
                  onReminder={onSendReminder}
                  onEdit={onOpenMagConfig}
                  onRegisterSpesa={onRegisterSpesaFromMag}
                  onOpenReport={onOpenReport}
                />
              ))}

              {hasMore && (
                <button
                  type="button"
                  onClick={() => setShowAll(true)}
                  className="w-full py-3 text-sm font-medium text-rose-600 hover:text-rose-700 flex items-center justify-center gap-1"
                >
                  Mostra altri ospiti
                  <ChevronDown className="w-4 h-4" />
                </button>
              )}

              {unconfiguredGuests.map((ospite) => (
                <div
                  key={ospite.id}
                  className="flex items-center justify-between border border-dashed border-slate-200 rounded-xl p-4 bg-slate-50/80"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-xs font-bold">
                      {guestInitials(ospite.nome_completo)}
                    </div>
                    <div>
                      <span className="font-medium text-slate-900">{ospite.nome_completo}</span>
                      {ospite.camera && (
                        <span className="text-xs text-slate-400 block">
                          {formatCameraAssegnata(ospite.camera) || ospite.camera}
                        </span>
                      )}
                      <span className="text-sm text-slate-500">Non configurato</span>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => onOpenMagConfig(ospite.id)}>
                    <Settings2 className="w-4 h-4 mr-1" /> Configura
                  </Button>
                </div>
              ))}

              {configuredGuests.length === 0 && unconfiguredGuests.length === 0 && (
                <p className="text-center text-slate-500 py-8">Nessun ospite attivo.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
